import React, { useEffect, useMemo, useState } from 'react';
import { ListBody, Page, PageHeaderBand, Spinner } from '../../ui/v2';

/**
 * Reservations chrome shared by both views.
 *
 * The title sits on the canvas; the OpenTable / links switch stays with it.
 * Each view fills the space underneath with a `ReservationsList`: its
 * figures, then the card with its toolbar band and table.
 * A view publishes its refresh label onto the title row.
 */
const ReservationsChromeContext = React.createContext<{
  setRefreshLabel: (label: string | null) => void;
} | null>(null);

export const ReservationsFrame: React.FC<{
  viewSwitch: React.ReactNode;
  children: React.ReactNode;
}> = ({ viewSwitch, children }) => {
  const [refreshLabel, setRefreshLabel] = useState<string | null>(null);
  const chrome = useMemo(() => ({ setRefreshLabel }), []);
  return (
    <ReservationsChromeContext.Provider value={chrome}>
      <Page
        title="Reservations"
        actions={
          <>
            {refreshLabel && (
              <span
                role="status"
                className="mr-2 hidden items-center gap-1.5 text-v2-12 text-v2-fg-3 sm:inline-flex"
              >
                <Spinner />
                {refreshLabel}
              </span>
            )}
            {viewSwitch}
          </>
        }
      >
        {children}
      </Page>
    </ReservationsChromeContext.Provider>
  );
};

const ReservationsList: React.FC<{
  refreshLabel?: string | null;
  summary?: React.ReactNode;
  toolbar?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ refreshLabel, summary, toolbar, children }) => {
  const chrome = React.useContext(ReservationsChromeContext);
  useEffect(() => {
    chrome?.setRefreshLabel(refreshLabel ?? null);
    return () => chrome?.setRefreshLabel(null);
  }, [chrome, refreshLabel]);

  return (
    <ListBody
      summary={summary}
      toolbar={toolbar ? <PageHeaderBand>{toolbar}</PageHeaderBand> : null}
    >
      {children}
    </ListBody>
  );
};

export default ReservationsList;
